// Hook personalizado para gestionar insumos

import { useState, useEffect, useCallback } from 'react'
import {
  getAllSupplies,
  getSupply,
  getSuppliesBySupplier,
  createSupply,
  updateSupply,
  updateSupplyPurchasePrice
} from '../services/supplies.service'

/**
 * Hook para obtener todos los insumos
 */
export const useSupplies = (includeInactive = false) => {
  const [supplies, setSupplies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSupplies = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getAllSupplies(includeInactive)
      setSupplies(data)
    } catch (err) {
      console.error('Error obteniendo insumos:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [includeInactive])

  useEffect(() => {
    fetchSupplies()
  }, [fetchSupplies])

  return { supplies, loading, error, refetch: fetchSupplies }
}

/**
 * Hook para obtener un insumo específico
 */
export const useSupply = (supplyId) => {
  const [supply, setSupply] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!supplyId) {
      setSupply(null)
      setLoading(false)
      return
    }

    const fetchSupply = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await getSupply(supplyId)
        setSupply(data)
      } catch (err) {
        console.error('Error obteniendo insumo:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchSupply()
  }, [supplyId])

  const refetch = useCallback(async () => {
    if (!supplyId) return
    try {
      setLoading(true)
      setError(null)
      const data = await getSupply(supplyId)
      setSupply(data)
    } catch (err) {
      console.error('Error obteniendo insumo:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [supplyId])

  return { supply, loading, error, refetch }
}

/**
 * Hook para obtener insumos por proveedor
 */
export const useSuppliesBySupplier = (supplierId) => {
  const [supplies, setSupplies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!supplierId) {
      setSupplies([])
      setLoading(false)
      return
    }

    const fetchSupplies = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await getSuppliesBySupplier(supplierId)
        setSupplies(data)
      } catch (err) {
        console.error('Error obteniendo insumos por proveedor:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchSupplies()
  }, [supplierId])

  return { supplies, loading, error }
}

/**
 * Hook para gestionar operaciones de insumos
 */
export const useSupplyOperations = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const create = useCallback(async (supplyData) => {
    try {
      setLoading(true)
      setError(null)
      const supplyId = await createSupply(supplyData)
      return supplyId
    } catch (err) {
      console.error('Error creando insumo:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const update = useCallback(async (supplyId, supplyData) => {
    try {
      setLoading(true)
      setError(null)
      await updateSupply(supplyId, supplyData)
    } catch (err) {
      console.error('Error actualizando insumo:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const updatePrice = useCallback(async (supplyId, purchasePrice) => {
    try {
      setLoading(true)
      setError(null)
      await updateSupplyPurchasePrice(supplyId, purchasePrice)
    } catch (err) {
      console.error('Error actualizando precio:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    create,
    update,
    updatePrice,
    loading,
    error
  }
}

